import { FarmRiskType, InlandMarineTypes, LiabilityType, lookupsJson } from '@ourbranch/lookups';

import farmUWQuestions from './branch/farm-uw-questions';

const allConstructionTypeIds = lookupsJson.farm.agriculturalConstructionType.map((ct) => ct.id);

export default {
  spinner: 'branch',
  // How long the newer-offer interstitial dwells before it redirects (#30994). It is the only
  // window staff have to reach "Keep version" and stay on the offer they opened, so Branch keeps the original 5s.
  offerRedirectInterstitialMs: 5000,
  featureFlags: {
    // Offer search dedupes on `webUserId` here (#34790). Farm brands opt in below to deduping on
    // the offer's policy types instead, because a farm customer can hold a FarmHome and a
    // FarmRental quote at once; Branch has no such pair, and its ~10.7M-doc offer index would be
    // undeduped until a full reindex finished.
    offerSearchDedupesByProductSet: false,
    // Underwriter insurance-score override is a mutuals-only control (CFM + Oakwood); Branch
    // does not rate on this credit tier the same way, so it is off here. (#34377)
    insuranceScoreOverride: false,
    // Protection-class override is a mutuals-only control (CFM, Forreston, Oakwood); off for Branch. (#35212)
    protectionClassOverride: false,
    // Descope-authority (CanOverrideCoverageLimit) override that LOCKS Coverage A & Coverage E limits
    // with a required internal reason, so a high 360Value estimate can be corrected and protected from
    // non-authority edits (#35617). Brand-gated so it can be turned off per-mutual; on for every brand
    // except Branch to start.
    coverageLimitOverride: false,
    applicantDateOfBirthAtNewOffer: false,
    // NI mailing-address state must match the agent's licensed states. Branch-only — farm
    // carriers (Forreston/Oakwood/CFM) relax this and enforce per-location instead. (#30876)
    enforceLicensedStateOnNamedInsured: true,
    // On Branch, the agency Portal remains gated by the `ShowAgencyPortal` cognito group
    // or `isAgency`; the mutual carriers opt in via this flag. (#33024)
    agencyPortalVisibleToAllUsers: false,
    // Letter of Experience generator (#29700): lets independent agencies self-serve the LOE
    // tool (in addition to internal staff granted the toolsGenerateLoe group). Branch-only for
    // now — the mutual carriers keep it off until their LOE letter format is confirmed.
    generateLoe: true,
    // Agency sweep accounts (#33155) on for Branch (#32606); management is additionally gated by
    // the CanManageSweepAccount cognito group so the pilot is controlled per agency principal.
    sweepAccounts: { enabledInNonProd: true, enabledInProd: true },
    // Agency Reports (#36219): the monthly Agency Performance Report PDFs Vantage drops in GCS,
    // surfaced from the account menu. On for internal Branch users; `visibleToAgencies` stays off
    // until agency principals (CanViewAgencyReports, #35833) should see their own stem's reports.
    // Mirrored in brand-config-be, which the lambda enforces. Read via core/helpers/agency-reports.js.
    agencyReports: { enabled: true, visibleToAgencies: false },
    // Underwriter MicroBilt-bypass on ACH adds (#33938) is likewise mutual-only; off on Branch.
    microBiltVerificationBypass: false,
    // "Collect payment information later" at bind (#33891) is likewise mutual-only; off on Branch.
    collectPaymentLater: false,
    // "Direct" auto payment method (#35123) is for migrated mutual auto policies; off on Branch retail.
    autoDirectBillPaymentMethod: false,
    // Branch auto quotes lean on the data pull for current-coverage details, so "Current Auto Details"
    // stays progressive-disclosure here — revealed only when the rater asks for it (#35337).
    showCurrentAutoDetailsUpFront: false,
    // Branch Auto does not require a companion Home/Farm policy, so the "Companion Policy" section
    // stays hidden (#35434).
    collectAutoCompanionPolicy: false,
    restrictNonRatedIndividualReasons: false,
    hideAutoCoverageInPast31Days: false,
    hideStressFreeSwitch: false,
    skipCheckoutOpposingPolicyQuestion: false,
    autoInterestedPartyTypes: false,
    // Branch discovers vehicles via the data pull, so the VIN section stays progressive-disclosure
    // here — revealed only when the rater asks for a VIN (#35337).
    showAddVehicleUpFront: false,
    // Branch's auto "Quote PDF" is the DocRaptor/Docamatic render of the customer /review page; the
    // Zywave DocGen auto quote exists only for brands with an `autoDocGenTemplateId` (CFM, #35921).
    autoQuotePdfViaDocGen: false,
    // Renters can be attached to a Branch Auto policy (the "Renters Coverage" section on the policy
    // Details → Auto screen). Branch-only — CFM never sold Renters, so the toggle is hidden there to
    // stop agents selecting it by mistake (#35383). Read via core/helpers/renters-on-auto.js.
    offerRentersOnAuto: true,
    // Deleting offer Supporting Documents (#34202) is mutual-only; off on Branch. Deletion is still
    // additionally gated by the CanDeleteInternalDocuments permission where the flag is on.
    deleteSupportingDocuments: false,
    // The staff "Send Invoice" billing action (#34203, #34419) is mutual-only; off on Branch. Where
    // on, it is additionally gated by the canEditPolicies permission.
    sendInvoice: false,
    // Keeping an applicant's insurance score away from agency users (#35046) is mutual-only; Branch
    // agents keep seeing it on the person cards.
    hideInsuranceScoreFromAgents: false,
    // Policy drafting (working drafts / draft-and-keep-changing endorsement flow) is
    // mutual-only; off on Branch. Consumed via core/helpers/policy-drafting.js — see that module.
    policyDrafts: false
  },
  draftablePolicyTypes: [],
  discounts: {
    hasFutureEffectiveDateDiscount: true,
    hasAdvanceQuoteDiscount: true,
    hasMultilineDiscount: true,
    hasPaperlessDiscount: true
  },
  farm: {
    uwQuestions: farmUWQuestions,
    IL: {
      liabilityExtensions: {
        rented: {
          [LiabilityType.Personal]: { locations: true, residences: true, structures: true },
          [LiabilityType.Commercial]: { locations: true, residences: true, structures: true }
        },
        occupied: {
          [LiabilityType.Personal]: { locations: true, residences: true },
          [LiabilityType.Commercial]: { locations: true, residences: true }
        },
        assets: {
          [LiabilityType.Personal]: { atv: true, golfCart: true, unmannedAircraft: true, boat: true },
          [LiabilityType.Commercial]: { atv: true, golfCart: true, unmannedAircraft: true, boat: true }
        }
      },
      risks: {
        [FarmRiskType.Fo3]: {
          hiddenCoverages: []
        },
        [FarmRiskType.Fo4]: {
          hiddenCoverages: [],
          allowedOptions: {
            inlandMarineItemTypes: [
              InlandMarineTypes.BoatDock,
              InlandMarineTypes.Boats,
              InlandMarineTypes.CoinCollection,
              InlandMarineTypes.FineArts,
              InlandMarineTypes.Furs,
              InlandMarineTypes.GolfEquipment,
              InlandMarineTypes.GolfCarts,
              InlandMarineTypes.Guns,
              InlandMarineTypes.Jewelry,
              InlandMarineTypes.LawnMowers,
              InlandMarineTypes.MiscellaneousProperty,
              InlandMarineTypes.MusicalInstruments,
              InlandMarineTypes.PersonalTools,
              InlandMarineTypes.Silverware,
              InlandMarineTypes.SportingEquipment,
              InlandMarineTypes.Tack,
              InlandMarineTypes.UnmannedAircraft,
              InlandMarineTypes.Hardware,
              InlandMarineTypes.Software,
              InlandMarineTypes.ScheduledGlass
            ]
          }
        },
        [FarmRiskType.Fo6Structures]: {
          // Coverage E EQ deductible was introduced for CFM MO (issue #32439); Branch's IL farm
          // product does not offer earthquake, so the new BrandFormField in FO6.jsx is hidden here.
          hiddenCoverages: ['deductibleEarthquake'],
          allowedOptions: {
            constructionTypes: allConstructionTypeIds
          },
          businessRules: {
            buildingClass1MaxAgeYears: 25
          }
        }
      }
    },
    MO: {
      liabilityExtensions: {
        rented: {
          [LiabilityType.Personal]: { locations: true, residences: true, structures: true },
          [LiabilityType.Commercial]: { locations: true, residences: true, structures: true }
        },
        occupied: {
          [LiabilityType.Personal]: { locations: true, residences: true },
          [LiabilityType.Commercial]: { locations: true, residences: true }
        },
        assets: {
          [LiabilityType.Personal]: { atv: true, golfCart: true, unmannedAircraft: true, boat: true },
          [LiabilityType.Commercial]: { atv: true, golfCart: true, unmannedAircraft: true, boat: true }
        }
      },
      risks: {
        [FarmRiskType.Fo3]: {
          hiddenCoverages: []
        },
        [FarmRiskType.Fo4]: {
          hiddenCoverages: []
        },
        [FarmRiskType.Fo6Structures]: {
          // Coverage E EQ deductible was introduced for CFM MO (issue #32439); Branch's MO farm
          // product does not offer earthquake, so the new BrandFormField in FO6.jsx is hidden here.
          hiddenCoverages: ['deductibleEarthquake'],
          allowedOptions: {
            constructionTypes: allConstructionTypeIds.filter((id) => id !== 'MH')
          },
          businessRules: {
            buildingClass1MaxAgeYears: 25
          }
        },
        [FarmRiskType.Fo6Unscheduled]: {
          hiddenCoverages: []
        },
        [FarmRiskType.Fo6Scheduled]: {
          hiddenCoverages: []
        }
      }
    }
  },
  agencyPortalRoutes: [
    {
      route: 'updates',
      src: 'https://portal.ourbranch.com/ebd/f79f122fa8bb4c6d80d82b4ebbb7e00a',
      title: 'Updates',
      icon: 'portal'
    },
    {
      route: 'underwriting',
      src: 'https://portal.ourbranch.com/ebd/c44e478eb85e4369ba6fa0ab07f26c74',
      title: 'Underwriting',
      icon: 'pen'
    },
    {
      route: 'tools',
      src: 'https://portal.ourbranch.com/ebd/98730a3c688c4c4b86b235ee84ce981e',
      title: 'Resources',
      icon: 'tool'
    },
    {
      route: 'training',
      src: 'https://portal.ourbranch.com/ebd/473fbb17df5f41bb9080164f25205d51',
      title: 'Training',
      icon: 'puzzlePiece'
    },
    {
      route: 'life-insurance',
      src: 'https://portal.ourbranch.com/ebd/81cac880cc984cd5b60fc15d64a8e10c',
      title: 'Life Insurance',
      icon: 'lifeInsurance'
    },
    {
      route: 'claims',
      src: 'https://portal.ourbranch.com/ebd/9a767baeb2c0464c80a847642629e720',
      title: 'Claims',
      icon: 'claim'
    },
    {
      route: 'contact-us',
      src: 'https://portal.ourbranch.com/ebd/29421786cff74f0e806dff853085cf19',
      title: 'Contact Us',
      icon: 'contact'
    }
  ]
};
